import {captureException} from '@sentry/browser';
import {Extras, ScopeContext, Primitive} from '@sentry/types';

import * as logging from 'util/Logging';

interface ErrorOptions {
  /** Override the Error to throw. Defaults to Error. */
  ErrorClass: new (message?: string) => Error;
  /** Override the prefix for error messages. Defaults to "Assertion Error: " */
  errorPrefix?: string;
}

const DEFAULT_ERROR_OPTIONS: ErrorOptions = {
  ErrorClass: Error,
  errorPrefix: 'Assertion Error: ',
};

function mergeScopeExtras(
  extra?: Extras,
  scope?: Partial<ScopeContext>,
  tags?: Record<string, Primitive>
): Partial<ScopeContext> {
  if (scope == null) {
    scope = {};
  }

  if (extra != null) {
    scope.extra = {...scope.extra, ...extra};
  }

  if (tags != null) {
    scope.tags = {...scope.tags, ...tags};
  }

  return scope;
}

// if condition is false, throws a new error
function assert(
  condition: boolean,
  message: string,
  extra?: Extras,
  scope?: Partial<ScopeContext>,
  errorOptions: Partial<ErrorOptions> = {}
): asserts condition {
  if (condition) {
    return;
  }
  const {ErrorClass, errorPrefix} = getDefaultedErrorOptions(errorOptions);
  const errorMessage = getErrorMessage(message, errorPrefix);
  const exception = new ErrorClass(errorMessage);
  captureException(exception, mergeScopeExtras(extra, scope));
  throw exception;
}

/**
 * @deprecated assertAndContinue is deprecated and should not be used in new code.

  The appropriate replacement will depend on the specifics of your use case. If you're unsure, err on the side
  of failing quickly and loudly. In the long run, crashes are less disruptive than data corruption.

  - In-development code:
      Just use assert. If you're worried about inadvertently crashing in production, that suggests a more
      urgent gap in feature flag coverage.
  - Incremental validation:
      If you're rolling out new validation to an existing production codepath and want to estimate impact,
      consider the checkSoftInvariant helper. This should always be temporary.
  - Graceful UI degradation:
      Consider using a React error boundary.
 */
function assertAndContinue(
  condition: boolean,
  message: string,
  extra?: Extras,
  scope?: Partial<ScopeContext>,
  errorOptions: Partial<ErrorOptions> = {}
): boolean {
  if (!condition) {
    const {ErrorClass, errorPrefix} = getDefaultedErrorOptions(errorOptions);
    const errorMessage = getErrorMessage(message, errorPrefix);
    logging.warn(errorMessage, mergeScopeExtras(extra, scope, {from_assert_and_continue: true}));
    if (window.debug || global.__NODE_TESTING__) {
      throw new ErrorClass(errorMessage);
    }
  }
  return condition;
}

function assertEqual<T>(
  actual: T,
  expected: T,
  message: string,
  extra?: Extras,
  scope?: Partial<ScopeContext>,
  errorOptions: Partial<ErrorOptions> = {}
): void {
  if (extra == null) {
    extra = {};
  }
  extra = {...extra, actual, expected};
  assert(actual === expected, message, extra, scope, errorOptions);
}

// Useful for type narrowing and chaining values that shouldn't be null
function assertNotNullAndReturn<T>(value: T): NonNullable<T> {
  assert(value != null, 'Value must not be null or undefined');
  return value;
}

/**
 * Only execute condition function if in debug mode
 * Useful for asserting expensive conditions outside of production
 */
function debugAssert(
  conditionCb: () => boolean,
  message: string,
  extra?: Extras,
  scope?: Partial<ScopeContext>,
  errorOptions: Partial<ErrorOptions> = {}
): void {
  if (window.debug || global.__NODE_TESTING__) {
    assert(conditionCb(), message, extra, scope, errorOptions);
  }
}

export {assert, assertAndContinue, assertEqual, assertNotNullAndReturn, debugAssert};

function getDefaultedErrorOptions(errorOptionOverrides: Partial<ErrorOptions>): ErrorOptions {
  return {...DEFAULT_ERROR_OPTIONS, ...errorOptionOverrides} as ErrorOptions;
}

function getErrorMessage(message: string, prefix?: string): string {
  return `${prefix ?? ''}${message}`;
}
